import {
  type CreateSubBranchRequest,
  type SubBranchDetail,
  SubBranchDetailSchema,
  type SubBranchListQuery,
  type SubBranchListResponse,
  SubBranchListResponseSchema,
  type UpdateSubBranchRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Sub-branch master API. Every response is validated with the shared Zod
 * schema so a server change can't sneak past the UI types.
 */

export const listSubBranches = async (
  query: SubBranchListQuery,
): Promise<SubBranchListResponse> => {
  const { data } = await apiClient.get<unknown>('/sub-branches', { params: query });
  return SubBranchListResponseSchema.parse(data);
};

export const getSubBranch = async (id: string): Promise<SubBranchDetail> => {
  const { data } = await apiClient.get<unknown>(`/sub-branches/${id}`);
  return SubBranchDetailSchema.parse(data);
};

export const createSubBranch = async (
  payload: CreateSubBranchRequest,
): Promise<SubBranchDetail> => {
  const { data } = await apiClient.post<unknown>('/sub-branches', payload);
  return SubBranchDetailSchema.parse(data);
};

export const updateSubBranch = async (
  id: string,
  payload: UpdateSubBranchRequest,
): Promise<SubBranchDetail> => {
  const { data } = await apiClient.patch<unknown>(`/sub-branches/${id}`, payload);
  return SubBranchDetailSchema.parse(data);
};

export const deleteSubBranch = async (id: string): Promise<void> => {
  await apiClient.delete(`/sub-branches/${id}`);
};

export const bulkDeleteSubBranches = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/sub-branches/bulk-delete', { ids });
  return data;
};

export const bulkSetSubBranchesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/sub-branches/bulk-set-status', { ids, isActive });
  return data;
};
